// Sistema de permisos por rol — Ziyup
//
// IMPORTANTE: Spectator se fusionó con Client en Feb 2026. Solo existen 4 roles
// públicos: barber, client, brand. El admin/founder_admin se asigna manualmente,
// nunca via registro público.
//
// La constante ROLES.SPECTATOR sigue exportada como ALIAS de 'client' para
// retrocompatibilidad de código heredado, pero no se debe crear NUEVOS usuarios
// con role='spectator'. La función `normalizeRole` convierte legacy 'spectator' → 'client'.

import { tr } from '@/contexts/LanguageContext';

export const ROLES = {
  BARBER: 'barber',
  CLIENT: 'client',
  // SPECTATOR es alias de CLIENT — mantenido para retrocompat. NO usar en código nuevo.
  SPECTATOR: 'client',
  BRAND: 'brand',
  // BARBERSHOP — barbería (negocio/local) con barberos afiliados por comisión (BS1).
  BARBERSHOP: 'barbershop',
  // JUDGE — rol de juez de torneos (asignado por admin, jamás via signup).
  JUDGE: 'judge',
  ADMIN: 'admin',
  // FOUNDER_ADMIN es alias de ADMIN — fusionado en Feb 2026 según pedido del usuario.
  // Ya no existe distinción en UI ni permisos. El email del fundador sigue siendo
  // identificable via isFounderUser() (utils/founder.js) para casos de seguridad
  // futuros, pero el role por defecto es 'admin'.
  FOUNDER_ADMIN: 'admin'
};

// Roles que un usuario PÚBLICO puede elegir al registrarse.
// Admin se asigna manualmente — nunca disponible en signup.
export const PUBLIC_REGISTRATION_ROLES = [ROLES.BARBER, ROLES.CLIENT, ROLES.BRAND, ROLES.BARBERSHOP];

// Roles considerados "administrativos" — sólo se asignan internamente.
export const ADMIN_ROLES = [ROLES.ADMIN];

// Convierte cualquier valor legacy a su rol canónico.
// 'spectator' → 'client'. 'founder_admin'/'super_admin' → 'admin'. Idempotente.
export const normalizeRole = (role) => {
  if (role === 'spectator') return ROLES.CLIENT;
  if (role === 'founder_admin') return ROLES.ADMIN;
  if (role === 'super_admin') return ROLES.ADMIN;
  return role;
};

// Helper para chequear si un usuario es admin
export const isAdminRole = (role) => normalizeRole(role) === ROLES.ADMIN;

// Defensa: clampea un rol a uno público. Si alguien intenta enviar role='admin'
// desde el formulario público, lo recortamos a 'client' (default seguro).
// Paso 1.2 (oct-2026): ningún correo habilita 'admin' desde el registro; el
// super_admin se crea por seed/DB y los admins se nombran desde el panel.
export const clampToPublicRole = (role) => {
  const r = normalizeRole(role);
  if (PUBLIC_REGISTRATION_ROLES.includes(r)) return r;
  // 'admin', 'super_admin', 'owner', 'platform_owner' u otros desconocidos → forzar a 'client'
  return ROLES.CLIENT;
};

// Verifica si un actor puede otorgar/revocar el rol admin a otro usuario.
// Cualquier admin (incluyendo el founder) puede hacerlo durante el desarrollo.
export const canGrantAdminRole = (actor) => {
  if (!actor) return false;
  return isAdminRole(actor.role);
};

export const PERMISSIONS = {
  // Barber
  CREATE_PROFILE: 'create_profile',
  UPLOAD_CONTENT: 'upload_content',
  APPEAR_RANKINGS: 'appear_rankings',

  // Cliente (incluye lo que era Spectator)
  DISCOVER_BARBERS: 'discover_barbers',
  VIEW_PROFILES: 'view_profiles',
  SEND_TIPS: 'send_tips',
  REQUEST_BOOKING: 'request_booking',
  WATCH_CONTENT: 'watch_content',
  EARN_POINTS: 'earn_points',
  REDEEM_POINTS: 'redeem_points',
  PURCHASE_PRODUCTS: 'purchase_products',
  PURCHASE_COURSES: 'purchase_courses',

  // Comunes
  LIKE_CONTENT: 'like_content',
  COMMENT: 'comment',
  FOLLOW: 'follow',
  SHARE: 'share',

  // Admin
  MANAGE_USERS: 'manage_users',
  APPROVE_POSTS: 'approve_posts',
  REJECT_POSTS: 'reject_posts',
  ENTER_RESULTS: 'enter_results',
  MODERATE_COMMENTS: 'moderate_comments',
  APPLY_SANCTIONS: 'apply_sanctions',
  MANAGE_RANKINGS: 'manage_rankings',
  MANAGE_JUDGES: 'manage_judges',
  MANAGE_AI_SETTINGS: 'manage_ai_settings',
  MANAGE_NOTIFICATIONS: 'manage_notifications',
  MANAGE_ADVERTISING: 'manage_advertising',
  MANAGE_SUBSCRIPTIONS: 'manage_subscriptions',

  // Brand
  CREATE_SPONSORED_TOURNAMENT: 'create_sponsored_tournament',
  SELL_PRODUCTS: 'sell_products',
  SPONSOR_BARBERS: 'sponsor_barbers',
  CREATE_CAMPAIGNS: 'create_campaigns',
  VIEW_SALES_ANALYTICS: 'view_sales_analytics',
  VIEW_SPONSORSHIP_CONTRACTS: 'view_sponsorship_contracts',

  // Founder (super admin)
  CREATE_OFFICIAL_TOURNAMENT: 'create_official_tournament',
  APPROVE_SPONSORED_TOURNAMENT: 'approve_sponsored_tournament',
  APPROVE_BRAND_PRODUCTS: 'approve_brand_products',
  APPROVE_BRAND_CAMPAIGNS: 'approve_brand_campaigns',
  MANAGE_BRANDS: 'manage_brands',
  MANAGE_COMMISSIONS: 'manage_commissions',
  MANAGE_PLATFORM: 'manage_platform'
};

const barberPermissions = [
  PERMISSIONS.CREATE_PROFILE,
  PERMISSIONS.UPLOAD_CONTENT,
  PERMISSIONS.APPEAR_RANKINGS,
  PERMISSIONS.LIKE_CONTENT,
  PERMISSIONS.COMMENT,
  PERMISSIONS.FOLLOW,
  PERMISSIONS.SHARE,
  PERMISSIONS.VIEW_PROFILES,
  PERMISSIONS.WATCH_CONTENT,
  PERMISSIONS.SEND_TIPS
];

// Cliente absorbe TODOS los permisos del antiguo Spectator (votar, watch, follow,
// share, view profiles, send tips) además de los nativos del cliente.
const clientPermissions = [
  PERMISSIONS.DISCOVER_BARBERS,
  PERMISSIONS.VIEW_PROFILES,
  PERMISSIONS.SEND_TIPS,
  PERMISSIONS.REQUEST_BOOKING,
  PERMISSIONS.LIKE_CONTENT,
  PERMISSIONS.COMMENT,
  PERMISSIONS.FOLLOW,
  PERMISSIONS.SHARE,
  PERMISSIONS.WATCH_CONTENT,
  PERMISSIONS.EARN_POINTS,
  PERMISSIONS.REDEEM_POINTS,
  PERMISSIONS.PURCHASE_PRODUCTS,
  PERMISSIONS.PURCHASE_COURSES
];

const brandPermissions = [
  PERMISSIONS.CREATE_SPONSORED_TOURNAMENT,
  PERMISSIONS.SELL_PRODUCTS,
  PERMISSIONS.SPONSOR_BARBERS,
  PERMISSIONS.CREATE_CAMPAIGNS,
  PERMISSIONS.VIEW_SALES_ANALYTICS,
  PERMISSIONS.VIEW_SPONSORSHIP_CONTRACTS,
  PERMISSIONS.WATCH_CONTENT,
  PERMISSIONS.LIKE_CONTENT,
  PERMISSIONS.COMMENT,
  PERMISSIONS.FOLLOW,
  PERMISSIONS.SHARE,
  PERMISSIONS.VIEW_PROFILES,
  PERMISSIONS.DISCOVER_BARBERS,
  PERMISSIONS.SEND_TIPS
];

const adminPermissions = [
  // Permisos administrativos (gestión)
  PERMISSIONS.MANAGE_USERS,
  PERMISSIONS.APPROVE_POSTS,
  PERMISSIONS.REJECT_POSTS,
  PERMISSIONS.ENTER_RESULTS,
  PERMISSIONS.MODERATE_COMMENTS,
  PERMISSIONS.APPLY_SANCTIONS,
  PERMISSIONS.MANAGE_RANKINGS,
  PERMISSIONS.MANAGE_JUDGES,
  PERMISSIONS.MANAGE_AI_SETTINGS,
  PERMISSIONS.MANAGE_NOTIFICATIONS,
  PERMISSIONS.MANAGE_ADVERTISING,
  PERMISSIONS.MANAGE_SUBSCRIPTIONS,
  // Permisos antes exclusivos del founder — fusionados al admin (Feb 2026)
  PERMISSIONS.CREATE_OFFICIAL_TOURNAMENT,
  PERMISSIONS.APPROVE_SPONSORED_TOURNAMENT,
  PERMISSIONS.APPROVE_BRAND_PRODUCTS,
  PERMISSIONS.APPROVE_BRAND_CAMPAIGNS,
  PERMISSIONS.MANAGE_BRANDS,
  PERMISSIONS.MANAGE_COMMISSIONS,
  PERMISSIONS.MANAGE_PLATFORM,
  // Admin también tiene todos los permisos de barber/client/brand
  ...barberPermissions,
  ...clientPermissions,
  ...brandPermissions
];

const barbershopPermissions = [
  // Fase D (sep-2026): la Barbería puede enviar Regalos en el feed igual que cliente/barbero.
  PERMISSIONS.SEND_TIPS
];

// Mantenido para retrocompatibilidad (rolePermissions['founder_admin'] hereda admin)
const founderAdminPermissions = adminPermissions;

export const rolePermissions = {
  [ROLES.BARBER]: barberPermissions,
  [ROLES.CLIENT]: clientPermissions,
  [ROLES.BRAND]: brandPermissions,
  [ROLES.BARBERSHOP]: barbershopPermissions,
  [ROLES.ADMIN]: adminPermissions,
  // 'founder_admin' como string literal (legacy) hereda admin para retrocompat
  founder_admin: founderAdminPermissions
};

export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;
  const role = normalizeRole(userRole);
  return rolePermissions[role]?.includes(permission) || false;
};

export const canUserPerformAction = (user, permission) => {
  if (!user || !user.role) return false;
  return hasPermission(user.role, permission);
};

export const getRoleLabel = (role) => {
  const r = normalizeRole(role);
  const labels = {
    [ROLES.BARBER]: tr("Barbero"),
    [ROLES.CLIENT]: tr("Cliente"),
    [ROLES.BRAND]: tr("Marca"),
    [ROLES.BARBERSHOP]: tr("Barbería"),
    [ROLES.JUDGE]: tr("Juez"),
    [ROLES.ADMIN]: 'Admin'
  };
  return labels[r] || r;
};

export const getRoleDescription = (role) => {
  const r = normalizeRole(role);
  const descriptions = {
    [ROLES.BARBER]: tr("Muestra tu trabajo, compite en batallas y construye tu reputación"),
    [ROLES.CLIENT]: tr("Descubre barberos, vota en batallas, gana puntos y compra productos o cursos"),
    [ROLES.BRAND]: tr("Patrocina barberos, vende productos, lanza campañas y solicita torneos"),
    [ROLES.ADMIN]: tr("Acceso total a la gestión de la plataforma")
  };
  return descriptions[r] || '';
};
